//Design system components & types
import { toast, type AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
//Types
import type { MenuListDataType } from 'src/components/Profiles/DropdownItem.types';
//Services
import { get_user_pin, pullDataWithLoading } from 'src/services/http';
//Stores
import { user_data, user_settings } from 'src/stores/account/user';
import { afterLoginParams } from 'src/stores/session/after-login-route';
import { profiles, showProfileModal } from 'src/stores/features/profiles';
import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
import { format } from 'svelte-i18n';
//SPA methods
import { push, replace } from 'svelte-spa-router';
//Svelte stores
import { get } from 'svelte/store';
import { capFirstChar } from './utilities';
import { getProfileDisplayName } from './houses/homes_helpers';
import { getLandingHref } from './main_menu';
import { loadingData } from 'src/stores/ui/loading-data';
import { clearCameraStores } from 'src/components/Camera/helpers/camera-helpers';
import { MAX_LOGGED_IN_PROFILES } from 'src/constants';
import { normalizeEmail } from 'src/helpers/email_helpers';

export const handleUserProfile: (string) => Promise<void> = async (hashPass) => {
	// Clear previous loadings
	loadingData.set(false);
	//Clear previous camera stores
	clearCameraStores();
	// Handle loading initial user data iwth loading spinner
	await pullDataWithLoading(); // calls => handleInitialLoad

	get_user_pin();

	// Load AI chat preferences for the new account
	aiSceneChatStore.loadChatPreferences();

	// Success
	const _format = get(format);
	const $profiles = get(profiles);
	const $user_data = get(user_data);
	const $user_settings = get(user_settings);

	const alreadyKnowProfile = $profiles.find(
		(p) => normalizeEmail(p.email) === normalizeEmail($user_data.email),
	);

	if ($user_settings?.new_user) {
		showProfileModal.set(true);
	}

	// console.log(
	// 	'[LOGIN] User profile: ', $user_data, $user_settings);
	if (typeof alreadyKnowProfile == 'undefined') {
		if ($profiles.length >= MAX_LOGGED_IN_PROFILES) {
			toast.open({
				type: 'info',
				title: _format('alert_title_profile_limit_reached', {
					default: 'Profiles limit reached',
				}),
				text: _format('alert_text_profiles_limit_reached', {
					default: 'You can not log in with more than 5 accounts at a time.',
				}),
			});
			replace(getLandingHref());
			return;
		}

		// Add new profile

		// Never surface a raw unit_id/user_id as the account name (cloud sometimes
		// returns it for never-renamed accounts); fall back to the account email.
		const profileTitle = getProfileDisplayName(
			$user_settings.name,
			$user_data.email,
			$user_data.user_id,
		);

		let accountAvatar: {
			backgroundColor?: AvatarColorVariantTypes;
			image?: string;
			text: string;
		} = {
			text: profileTitle[0] ?? '',
		};

		accountAvatar = {
			...accountAvatar,
			...$user_settings.acc_avatar,
		};

		const newProfile: MenuListDataType = {
			title: profileTitle,
			acc_avatar: {
				...accountAvatar,
				avatarOptions: {
					type: $user_data.premium ? 'premium' : null,
				},
			},
			isSelected: true,
			id: $user_data.user_id,
			email: $user_data.email,
			pass: hashPass,
			installer: !!$user_data.installer,
		};

		profiles.add(newProfile);

		toast.open({
			type: 'success',
			title: capFirstChar(_format('alert_title_success', { default: 'Success' })),
			text: capFirstChar(
				_format('alert_logged_in_account', {
					values: { accountName: $user_data.email },
					default: `Logged in account ${$user_data.email}`,
				}),
			),
		});
	} else {
		profiles.change(alreadyKnowProfile.email);
	}

	// Redirect after login
	const redirectUrl = afterLoginParams.getURL();
	if (redirectUrl) {
		console.log('[LOGIN] Redirect after login: ', redirectUrl);
		replace(getLandingHref());
		push(redirectUrl);
		afterLoginParams.reset();
	} else {
		replace(getLandingHref());
	}

	return;
};

export const removeProfile: (email: string) => void | MenuListDataType = (email) => {
	profiles.remove(email);

	if (get(profiles).length === 0) {
		profiles.reset();
		replace('/login');
		return;
	} else {
		return get(profiles)[0];
	}
};
